import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/auth.store';
import { NotificationProvider } from './contexts/NotificationContext';
import { startPage } from './lib/preferences';

// Layout
import Layout from './components/layout/Layout';
import ProtectedRoute, { RequirePermission, StartRedirect } from './components/common/ProtectedRoute';

// Screens
import Login from './screens/auth/Login';
import Register from './screens/auth/Register';
import Dashboard from './screens/dashboard/Dashboard';
import InventoryList from './screens/inventory/InventoryList';
import Warehouses from './screens/inventory/Warehouses';
import StockTransfers from './screens/inventory/StockTransfers';
import GoodsReceipts from './screens/purchase/GoodsReceipts';
import Routings from './screens/manufacturing/Routings';
import JobCards from './screens/manufacturing/JobCards';
import Oee from './screens/manufacturing/Oee';
import Mrp from './screens/manufacturing/Mrp';
import Capacity from './screens/manufacturing/Capacity';
import Instruments from './screens/quality/Instruments';
import PartApproval from './screens/quality/PartApproval';
import Maintenance from './screens/maintenance/Maintenance';
import Tooling from './screens/tooling/Tooling';
import Dispatches from './screens/sales/Dispatches';
import Payments from './screens/finance/Payments';
import Payables from './screens/finance/Payables';
import Inspections from './screens/quality/Inspections';
import NonConformances from './screens/quality/NonConformances';
import Scrap from './screens/manufacturing/Scrap';
import Reservations from './screens/inventory/Reservations';
import Batches from './screens/inventory/Batches';
import Serials from './screens/inventory/Serials';
import StockValuation from './screens/inventory/StockValuation';
import EngineeringChanges from './screens/master-data/EngineeringChanges';
import Indents from './screens/purchase/Indents';
import Rfqs from './screens/purchase/Rfqs';
import ApprovedVendors from './screens/vendors/ApprovedVendors';
import Enquiries from './screens/sales/Enquiries';
import CostSheets from './screens/sales/CostSheets';
import RateContracts from './screens/sales/RateContracts';
import CustomerSchedules from './screens/sales/CustomerSchedules';
import PurchaseList from './screens/purchase/PurchaseList';
import SalesList from './screens/sales/SalesList';
import SalesDashboard from './screens/sales/SalesDashboard';
import CustomerList from './screens/customers/CustomerList';
import ManufacturingList from './screens/manufacturing/ManufacturingList';
import InvoiceList from './screens/invoices/InvoiceList';
import HSNCodeList from './screens/hsn-codes/HSNCodeList';
import AnalyticsDashboard from './screens/analytics/AnalyticsDashboard';
import GSTReports from './screens/reports/GSTReports';
import Reports from './screens/reports/Reports';
import Settings from './screens/settings/Settings';
import NotificationsList from './screens/notifications/NotificationsList';
import BackupManager from './screens/backup/BackupManager';
import AuditLog from './screens/audit/AuditLog';
import Documents from './screens/documents/Documents';
import Scan from './screens/labels/Scan';
import Labels from './screens/labels/Labels';
import ShopFloor from './screens/manufacturing/ShopFloor';
import Integrations from './screens/integrations/Integrations';
import Portal from './screens/portal/Portal';
import Hr from './screens/hr/Hr';
import MISDashboard from './screens/mis/MISDashboard';
import UserList from './screens/users/UserList';
import UserProfile from './screens/users/UserProfile';
import RoleManagement from './pages/RoleManagement';
import CompanySettings from './screens/settings/CompanySettings';
import MasterDataDashboard from './screens/master-data/MasterDataDashboard';
import CompanyManagement from './screens/master-data/CompanyManagement';
import PlantManagement from './screens/master-data/PlantManagement';
import WorkCenterManagement from './screens/master-data/WorkCenterManagement';
import CostCenterManagement from './screens/master-data/CostCenterManagement';
import UoMManagement from './screens/master-data/UoMManagement';
import ItemTypeManagement from './screens/master-data/ItemTypeManagement';
import ItemAttributeManagement from './screens/master-data/ItemAttributeManagement';
import BOMManagement from './screens/master-data/BOMManagement';
import GstRateManagement from './screens/master-data/GstRateManagement';
import VendorList from './screens/vendors/VendorList';
import PriceListManagement from './screens/sales/PriceListManagement';
import DemandForecastingScreen from './components/screens/DemandForecastingScreen';
import QuoteManagement from './components/sales/QuoteManagement';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { checkAuth, isLoading, user, isAuthenticated } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // A refused request means the role changed since the page loaded: read it again so the menu and
  // the pages follow what the user may do now
  useEffect(() => {
    const refresh = () => checkAuth();
    window.addEventListener('auth:forbidden', refresh);
    return () => window.removeEventListener('auth:forbidden', refresh);
  }, [checkAuth]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // A customer or vendor signed in to the portal gets the portal and nothing else
  if (isAuthenticated && user?.portal) {
    return (
      <QueryClientProvider client={queryClient}>
        <Router>
          <Toaster position="top-right" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/portal" element={<Portal />} />
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>
        </Router>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <NotificationProvider>
        <Router>
          <div className="App">
            <Toaster position="top-right" />
          <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<StartRedirect preferred={startPage()} />} />
              <Route path="dashboard" element={<RequirePermission permission="analytics.read"><Dashboard /></RequirePermission>} />
              <Route path="inventory" element={<RequirePermission permission="inventory.read"><InventoryList /></RequirePermission>} />
              <Route path="warehouses" element={<RequirePermission permission="inventory.read"><Warehouses /></RequirePermission>} />
              <Route path="stock-transfers" element={<RequirePermission permission="inventory.read"><StockTransfers /></RequirePermission>} />
              <Route path="reservations" element={<RequirePermission permission="inventory.read"><Reservations /></RequirePermission>} />
              <Route path="stock-valuation" element={<RequirePermission permission="inventory.read"><StockValuation /></RequirePermission>} />
              <Route path="serials" element={<RequirePermission permission="inventory.read"><Serials /></RequirePermission>} />
              <Route path="batches" element={<RequirePermission permission="inventory.read"><Batches /></RequirePermission>} />
              <Route path="approved-vendors" element={<RequirePermission permission="vendors.read"><ApprovedVendors /></RequirePermission>} />
              <Route path="indents" element={<RequirePermission permission="purchase.read"><Indents /></RequirePermission>} />
              <Route path="rfqs" element={<RequirePermission permission="purchase.read"><Rfqs /></RequirePermission>} />
              <Route path="goods-receipts" element={<RequirePermission permission="purchase.read"><GoodsReceipts /></RequirePermission>} />
              <Route path="routings" element={<RequirePermission permission="manufacturing.read"><Routings /></RequirePermission>} />
              <Route path="capacity" element={<RequirePermission permission="manufacturing.read"><Capacity /></RequirePermission>} />
              <Route path="mrp" element={<RequirePermission permission="manufacturing.read"><Mrp /></RequirePermission>} />
              <Route path="oee" element={<RequirePermission permission="manufacturing.read"><Oee /></RequirePermission>} />
              <Route path="job-cards" element={<RequirePermission permission="manufacturing.read"><JobCards /></RequirePermission>} />
              <Route path="dispatches" element={<RequirePermission permission="sales.read"><Dispatches /></RequirePermission>} />
              <Route path="payments" element={<RequirePermission permission="gst.read"><Payments /></RequirePermission>} />
              <Route path="payables" element={<RequirePermission permission="purchase.read"><Payables /></RequirePermission>} />
              <Route path="tooling" element={<RequirePermission permission="manufacturing.read"><Tooling /></RequirePermission>} />
              <Route path="maintenance" element={<RequirePermission permission="manufacturing.read"><Maintenance /></RequirePermission>} />
              <Route path="part-approval" element={<RequirePermission permission="quality.read"><PartApproval /></RequirePermission>} />
              <Route path="instruments" element={<RequirePermission permission="quality.read"><Instruments /></RequirePermission>} />
              <Route path="inspections" element={<RequirePermission permission="quality.read"><Inspections /></RequirePermission>} />
              <Route path="ncrs" element={<RequirePermission permission="quality.read"><NonConformances /></RequirePermission>} />
              <Route path="scrap" element={<RequirePermission permission="inventory.read"><Scrap /></RequirePermission>} />
              <Route path="purchase" element={<RequirePermission permission="purchase.read"><PurchaseList /></RequirePermission>} />
              <Route path="sales" element={<RequirePermission permission="sales.read"><SalesList /></RequirePermission>} />
              <Route path="sales-dashboard" element={<RequirePermission permission="sales.read"><SalesDashboard /></RequirePermission>} />
              <Route path="price-lists" element={<RequirePermission permission="sales.read"><PriceListManagement /></RequirePermission>} />
              <Route path="rate-contracts" element={<RequirePermission permission="sales.read"><RateContracts /></RequirePermission>} />
              <Route path="customer-schedules" element={<RequirePermission permission="sales.read"><CustomerSchedules /></RequirePermission>} />
              <Route path="cost-sheets" element={<RequirePermission permission="sales.read"><CostSheets /></RequirePermission>} />
              <Route path="enquiries" element={<RequirePermission permission="sales.read"><Enquiries /></RequirePermission>} />
              <Route path="quotes" element={<RequirePermission permission="sales.read"><QuoteManagement /></RequirePermission>} />
              <Route path="demand-forecasting" element={<RequirePermission permission="sales.read"><DemandForecastingScreen /></RequirePermission>} />
              <Route path="customers" element={<RequirePermission permission="customers.read"><CustomerList /></RequirePermission>} />
              <Route path="vendors" element={<RequirePermission permission="vendors.read"><VendorList /></RequirePermission>} />
              <Route path="manufacturing" element={<RequirePermission permission="manufacturing.read"><ManufacturingList /></RequirePermission>} />
              <Route path="invoices" element={<RequirePermission permission="gst.read"><InvoiceList /></RequirePermission>} />
              <Route path="hsn-codes" element={<RequirePermission permission="gst.read"><HSNCodeList /></RequirePermission>} />
              <Route path="analytics" element={<RequirePermission permission="analytics.read"><AnalyticsDashboard /></RequirePermission>} />
              <Route path="mis" element={<RequirePermission permission="mis.read"><MISDashboard /></RequirePermission>} />
              <Route path="gst-reports" element={<RequirePermission permission="gst.read"><GSTReports /></RequirePermission>} />
              <Route path="reports" element={<RequirePermission permission="reports.read"><Reports /></RequirePermission>} />
              <Route path="notifications" element={<NotificationsList />} />
              <Route path="integrations" element={<RequirePermission permission="integrations.read"><Integrations /></RequirePermission>} />
              <Route path="audit-log" element={<RequirePermission permission="audit.read"><AuditLog /></RequirePermission>} />
              <Route path="backup" element={<RequirePermission permission="backup.read"><BackupManager /></RequirePermission>} />
              <Route path="users" element={<RequirePermission permission="users.read"><UserList /></RequirePermission>} />
              <Route path="roles" element={<RequirePermission permission="roles.read"><RoleManagement /></RequirePermission>} />
              <Route path="profile" element={<UserProfile />} />
              <Route path="company-settings" element={<RequirePermission permission="settings.read"><CompanySettings /></RequirePermission>} />
              <Route path="settings" element={<Settings />} />
              
              {/* Master Data Routes */}
              <Route path="master-data" element={<RequirePermission permission="master_data.read"><MasterDataDashboard /></RequirePermission>} />
              <Route path="master-data/companies" element={<RequirePermission permission="master_data.read"><CompanyManagement /></RequirePermission>} />
              <Route path="master-data/plants" element={<RequirePermission permission="master_data.read"><PlantManagement /></RequirePermission>} />
              <Route path="master-data/work-centers" element={<RequirePermission permission="master_data.read"><WorkCenterManagement /></RequirePermission>} />
              <Route path="master-data/cost-centers" element={<RequirePermission permission="master_data.read"><CostCenterManagement /></RequirePermission>} />
              <Route path="master-data/uoms" element={<RequirePermission permission="master_data.read"><UoMManagement /></RequirePermission>} />
              <Route path="master-data/item-types" element={<RequirePermission permission="master_data.read"><ItemTypeManagement /></RequirePermission>} />
              <Route path="master-data/item-attributes" element={<RequirePermission permission="master_data.read"><ItemAttributeManagement /></RequirePermission>} />
              <Route path="master-data/boms" element={<RequirePermission permission="master_data.read"><BOMManagement /></RequirePermission>} />
              <Route path="hr" element={<RequirePermission permission="hr.read"><Hr /></RequirePermission>} />
              <Route path="shop-floor" element={<RequirePermission permission="manufacturing.read"><ShopFloor /></RequirePermission>} />
              <Route path="scan" element={<Scan />} />
              <Route path="labels" element={<Labels />} />
              <Route path="documents" element={<RequirePermission permission="documents.read"><Documents /></RequirePermission>} />
              <Route path="master-data/ecns" element={<RequirePermission permission="master_data.read"><EngineeringChanges /></RequirePermission>} />
              <Route path="master-data/gst-rates" element={<RequirePermission permission="master_data.read"><GstRateManagement /></RequirePermission>} />
            </Route>
          </Routes>
          </div>
        </Router>
      </NotificationProvider>
    </QueryClientProvider>
  );
}

export default App;